/* oxlint-disable tailwindcss/no-unknown-classes -- Styles are colocated in antithesis-simulation.css. */
import { Database, Pause, Play } from "lucide-react"
import { useEffect, useId, useRef, useState, type CSSProperties } from "react"

import { EntireIcon } from "@/components/primitives/icons/EntireIcon"
import { useElementWidth } from "@/hooks/useElementWidth"
import { useIsVisible } from "@/hooks/useIsVisible"
import { usePrefersReducedMotion } from "@/hooks/usePrefersReducedMotion"
import { cardRingClassName } from "@/routes/-components/home-new/homeNewStyles"

import { DiagramTooltip } from "./diagram-tooltip"
import { antithesisCaption } from "./forge-diagram-steps"

import "./antithesis-simulation.css"

/*
 * Deterministic simulation testing of an EntireDB cell. Three Git clients keep
 * sending operations into a six-node cell spread over three availability zones,
 * every node backed by one shared PostgreSQL, while a seeded fault injector
 * kills and pauses nodes and cuts node-to-store links.
 *
 * Two authored scenes rather than one scaled down: a landscape scene for the
 * article column, and a stacked scene once the figure is narrower than
 * `stackedBreakpoint`. Accent-blue dots ride the connectors at a constant speed per
 * scene; faulted parts turn red and carry no traffic.
 */

type NodeFaultKind = "down" | "none" | "paused"

interface NodeFault {
  kind: NodeFaultKind
  until: number
}

interface Operation {
  /** When the dot lands: at the node for a request, at the client for a response. */
  arrivesAt: number
  failed: boolean
  isActive: boolean
  /** Whether the dot rides the response route, carrying data back to the client. */
  isResponse: boolean
  /** How long after the operation fails its fault mark appears, in ms. */
  markDelayMs: number
  readyAt: number
  /** Increments on every dispatch so the flow dot restarts from its source. */
  serial: number
  target: number
}

interface SimulationState {
  elapsed: number
  links: number[]
  nextFaultAt: number
  nodes: NodeFault[]
  ops: Operation[]
  seed: number
}

/** How long a dot takes to travel an operation's route, in ms. */
type TravelTime = (clientIndex: number, nodeIndex: number) => number

const stackedBreakpoint = 560

const clientLabels = ["push", "fetch", "clone"] as const
// Push is a one-way write, so its dot rides the request route down to the
// node; fetch and clone carry their data back up the response route.
const pushClientIndex = clientLabels.indexOf("push")

const clientsTipTitle = "Git clients"
const clientsTip =
  "Simulated Git clients keep working the cell — cloning, fetching, pushing — while faults are injected underneath them."

const zoneLabels = ["AZ-A", "AZ-B", "AZ-C"] as const
const nodeLabels = zoneLabels.flatMap((_, zone) =>
  [0, 1].map((slot) => `node ${"abc"[zone]}${slot + 1}`),
)
const nodeCount = nodeLabels.length
const storeLabel = "POSTGRESQL"
const cellLabel = "ENTIREDB CELL"

// Labels are set in JetBrains Mono, whose fixed 0.6em advance lets a
// chip hug its own label without measuring text at render time.
function labelWidth(text: string, fontSize: number) {
  return text.length * fontSize * 0.6
}

function capBandOffset(fontSize: number) {
  return fontSize * 0.347
}

function centredBaseline(top: number, height: number, fontSize: number) {
  return top + height / 2 + capBandOffset(fontSize)
}

function cardTopEdge(x: number, top: number, width: number, radius: number) {
  return [
    `M${x} ${top + radius}`,
    `A${radius} ${radius} 0 0 1 ${x + radius} ${top}`,
    `H${x + width - radius}`,
    `A${radius} ${radius} 0 0 1 ${x + width} ${top + radius}`,
  ].join(" ")
}

function clusterX(widths: number[], centerX: number, gap: number) {
  const total = widths.reduce((sum, width) => sum + width, 0) + gap * (widths.length - 1)
  const xs: number[] = []
  let x = centerX - total / 2
  for (const width of widths) {
    xs.push(x)
    x += width + gap
  }
  return xs
}

function at<T>(values: readonly T[], index: number): T {
  const value = values[index]
  if (value === undefined) throw new Error(`Missing index ${index}`)
  return value
}

/** Length of a path built only from M, V, H and quarter-circle A commands. */
function pathLength(d: string): number {
  const tokens = d.match(/[MVHA]|-?\d+\.?\d*/g) ?? []
  let cmd = ""
  let cx = 0
  let cy = 0
  let total = 0
  const nums: number[] = []
  for (const token of tokens) {
    if (token === "M" || token === "V" || token === "H" || token === "A") {
      cmd = token
      nums.length = 0
      continue
    }
    nums.push(Number(token))
    if (cmd === "M" && nums.length === 2) {
      cx = at(nums, 0)
      cy = at(nums, 1)
      nums.length = 0
    } else if (cmd === "V" && nums.length === 1) {
      total += Math.abs(at(nums, 0) - cy)
      cy = at(nums, 0)
      nums.length = 0
    } else if (cmd === "H" && nums.length === 1) {
      total += Math.abs(at(nums, 0) - cx)
      cx = at(nums, 0)
      nums.length = 0
    } else if (cmd === "A" && nums.length === 7) {
      total += (at(nums, 0) * Math.PI) / 2
      cx = at(nums, 5)
      cy = at(nums, 6)
      nums.length = 0
    }
  }
  return total
}

function buildMarks(size: number) {
  const inset = size * 0.1
  const barWidth = size * 0.3
  const barGap = size * 0.145
  const half = size / 2
  return {
    kill: `M${-half + inset} ${-half + inset}L${half - inset} ${half - inset}M${-half + inset} ${half - inset}L${half - inset} ${-half + inset}`,
    pause: `M${-barGap - barWidth} ${-half} h${barWidth} v${size} h${-barWidth} z M${barGap} ${-half} h${barWidth} v${size} h${-barWidth} z`,
  }
}

function buildElbowRoute(
  fromX: number,
  fromY: number,
  toX: number,
  toY: number,
  y: number,
  radius: number,
) {
  const fit = Math.min(radius, Math.abs(y - fromY), Math.abs(toY - y), Math.abs(toX - fromX) / 2)
  const r = Math.round(fit * 1000) / 1000
  if (r <= 0) return `M${fromX} ${fromY} V${y} H${toX} V${toY}`

  const across = Math.sign(toX - fromX)
  const down = Math.sign(y - fromY)
  const land = Math.sign(toY - y)

  return [
    `M${fromX} ${fromY}`,
    `V${y - down * r}`,
    `A${r} ${r} 0 0 ${across === -down ? 1 : 0} ${fromX + across * r} ${y}`,
    `H${toX - across * r}`,
    `A${r} ${r} 0 0 ${land === across ? 1 : 0} ${toX} ${y + land * r}`,
    `V${toY}`,
  ].join(" ")
}

// ---------- seeded random ----------

/** Mulberry32: a small seeded generator whose state can be saved and resumed. */
function createRandom(seed: number) {
  let state = seed
  return {
    next() {
      state = (state + 0x6d2b79f5) | 0
      let mixed = Math.imul(state ^ (state >>> 15), 1 | state)
      mixed = (mixed + Math.imul(mixed ^ (mixed >>> 7), 61 | mixed)) ^ mixed
      return ((mixed ^ (mixed >>> 14)) >>> 0) / 4294967296
    },
    get seed() {
      return state
    },
  }
}

type Random = ReturnType<typeof createRandom>

// ---------- flow dots ----------

const flowVirtualLength = 100
const flowDotsPerWire = 3
const flowDelayRandom = createRandom(1)

interface FabricFlow {
  d: string
  delay: number
  length: number
}

/** Node-to-store routes with a fixed stagger, so re-renders never restart them. */
function buildFabricFlows(paths: string[], speed: number): FabricFlow[] {
  return paths.map((d) => {
    const length = pathLength(d)
    return { d, delay: -flowDelayRandom.next() * (length / speed), length }
  })
}

/** Three dots looping a node's route to PostgreSQL. */
function LoopFlow({
  dotLength,
  flow,
  speed,
}: {
  dotLength: number
  flow: FabricFlow
  speed: number
}) {
  const period = flowVirtualLength / flowDotsPerWire
  const dash = (dotLength / flow.length) * flowVirtualLength
  return (
    <path
      className="ats-flow ats-flow--loop"
      d={flow.d}
      pathLength={flowVirtualLength}
      style={{
        animationDelay: `${flow.delay}s`,
        animationDuration: `${flow.length / speed}s`,
        strokeDasharray: `${dash} ${period - dash}`,
      }}
    />
  )
}

/** One dot travelling an operation's route once, from its first point to its last. */
function OnceFlow({
  d,
  dotLength,
  isFault,
  speed,
}: {
  d: string
  dotLength: number
  isFault: boolean
  speed: number
}) {
  const length = pathLength(d)
  const dash = (dotLength / length) * flowVirtualLength
  return (
    <path
      className={`ats-flow ats-flow--once${isFault ? " ats-flow--fault" : ""}`}
      d={d}
      pathLength={flowVirtualLength}
      style={
        {
          "--ats-flow-from": dash,
          animationDuration: `${(length + dotLength) / speed}s`,
          strokeDasharray: `${dash} ${flowVirtualLength * 2}`,
        } as CSSProperties
      }
    />
  )
}

function travelMs(d: string, dotLength: number, speed: number) {
  return ((pathLength(d) + dotLength) / speed) * 1000
}

// ---------- landscape scene geometry (1920 × 872) ----------

const sceneTop = 164
const sceneWidth = 1920
const sceneHeight = 872
const fontSize = 30
const flowSpeed = 480
const flowDotLength = 1

const clientTop = 164
const clientHeight = 64
const clientExitY = clientTop + clientHeight
const clientPadX = 32
const clientGap = 24
const panelRadius = 16
const routeCorner = 14
const routeChannel = 401

const cellX = 56.5
const cellWidth = 1808
const cellHeaderY = 307.5
const cellBodyTop = 388
const cellHeaderHeight = cellBodyTop - cellHeaderY
const cellHeaderRadius = 24.5
const cellBottom = 1021
const cellBodyRadius = 24
const cellIconX = cellX + 30
const cellIconSize = 30
const cellLabelX = cellIconX + cellIconSize + 20
const cellCenterX = cellX + cellWidth / 2

const zoneX = [103.5, 691.5, 1279.5] as const
const zoneWidth = 539
const zoneTop = 440.5
const zoneBandHeight = 79.5
const zoneCardTop = zoneTop + zoneBandHeight
const zoneHeight = 257.5
const zoneRadius = 24
const zoneLabelInset = 26

const nodeInset = 25
const nodeWidth = 231
const nodeGap = 28
const nodeTop = 545
const nodeHeight = 128
const nodeBottom = nodeTop + nodeHeight
const nodeRadius = 16
const markSize = 34
const markCenterY = nodeTop + nodeHeight / 2

const busY = 750
const storeWidth = 1720
const storeHeight = 98
const storeTop = 877.5
const storeRadius = 23.5
const storeIconSize = 30
const storeIconGap = 16
const storeHug = 30

const arrowOverhang = 1.4
const fabricTail = 10

const clientWidths = clientLabels.map((label) => labelWidth(label, fontSize) + clientPadX * 2)
const clientXs = clusterX(clientWidths, cellCenterX, clientGap)
const nodeXs = zoneX.flatMap((x) =>
  [0, 1].map((slot) => x + nodeInset + slot * (nodeWidth + nodeGap)),
)
const marks = buildMarks(markSize)

function clientCenterX(index: number) {
  return at(clientXs, index) + at(clientWidths, index) / 2
}

function nodeCenterX(index: number) {
  return at(nodeXs, index) + nodeWidth / 2
}

function nodeTurn(index: number) {
  if (index === 0) return 1
  if (index === nodeCount - 1) return -1
  return 0
}

function nodeLinkPath(index: number) {
  const cx = nodeCenterX(index)
  const turn = nodeTurn(index)
  if (turn === 0) return `M${cx} ${nodeBottom} V${busY}`
  return `M${cx} ${nodeBottom} V${busY - routeCorner} A${routeCorner} ${routeCorner} 0 0 ${turn === 1 ? 0 : 1} ${cx + turn * routeCorner} ${busY}`
}

function nodeStorePath(index: number) {
  const toStore = ` H${cellCenterX} V${storeTop - arrowOverhang}`
  return nodeLinkPath(index) + toStore
}

function requestRoute(clientIndex: number, nodeIndex: number, endY = nodeTop) {
  return buildElbowRoute(
    clientCenterX(clientIndex),
    clientExitY,
    nodeCenterX(nodeIndex),
    endY,
    routeChannel,
    routeCorner,
  )
}

function responseRoute(clientIndex: number, nodeIndex: number) {
  return buildElbowRoute(
    nodeCenterX(nodeIndex),
    nodeTop,
    clientCenterX(clientIndex),
    clientExitY,
    routeChannel,
    routeCorner,
  )
}

const staticRoutes = [
  requestRoute(0, 1, nodeTop - arrowOverhang),
  requestRoute(1, 2, nodeTop - arrowOverhang),
  requestRoute(2, 5, nodeTop - arrowOverhang),
]

const fabricFlows = buildFabricFlows(
  nodeLabels.map((_, index) => nodeStorePath(index)),
  flowSpeed,
)

const landscapeTravel: TravelTime = (clientIndex, nodeIndex) =>
  travelMs(requestRoute(clientIndex, nodeIndex), flowDotLength, flowSpeed)

// ---------- stacked scene geometry (360 × 526) ----------

const mSceneWidth = 360
const mFontSize = 11
const mFlowSpeed = 170
const mFlowDotLength = 0.5
const mOuterPad = 14
const mGutterWidth = 16
const mCellPad = 8

const mCellX = mOuterPad + mGutterWidth
const mCellWidth = mSceneWidth - mCellX * 2
const mZoneX = mCellX + mCellPad
const mZoneWidth = mCellWidth - mCellPad * 2

// The left margin carries client traffic (outer lane) and node 1's store
// traffic (inner lane); the right margin carries only node 2's store traffic.
const mLeftGutterX = mCellPad
const mLeftStoreGutterX = mCellX - mCellPad
const mRightGutterX = mSceneWidth - mCellX / 2

const mNodeInset = 10
const mNodeGap = 8
const mNodeWidth = (mZoneWidth - mNodeInset * 2 - mNodeGap) / 2
const mNodeHeight = 38
const mZoneHeaderHeight = 34
const mZoneBodyHeight = mNodeInset * 2 + mNodeHeight
const mZoneHeight = mZoneHeaderHeight + mZoneBodyHeight
const mZoneGapY = 10

const mClientY = 14
const mClientHeight = 26
const mClientPadX = 10
const mClientGap = 10
const mCellCenterX = mCellX + mCellWidth / 2
const mClientWidths = clientLabels.map((label) => labelWidth(label, mFontSize) + mClientPadX * 2)
const mClientXs = clusterX(mClientWidths, mCellCenterX, mClientGap)
const mClientExitY = mClientY + mClientHeight
const mGutterTopY = mClientExitY + 20

const mCellHeaderHeight = mZoneHeaderHeight
const mCellY = mGutterTopY + 18
const mCellHeaderBottom = mCellY + mCellHeaderHeight
const mZoneTopPad = 10
const mZoneY = [0, 1, 2].map((i) => mCellHeaderBottom + mZoneTopPad + i * (mZoneHeight + mZoneGapY))
const mCellBottom = at(mZoneY, 2) + mZoneHeight + 10

const mStoreX = mCellX
const mStoreWidth = mCellWidth
const mStoreY = mCellBottom + 48
const mStoreHeight = 36
const mStoreCenterX = mStoreX + mStoreWidth / 2
const mStoreMergeY = mCellBottom + 24
const mSceneHeight = mStoreY + mStoreHeight + mOuterPad

const mRadius = 10
const mPanelRadius = 8
const mStoreIconSize = 16
const mStoreIconGap = 8
const mStoreHug = 14
const mCellIconSize = 16
const mCellIconGap = 8
const mCellIconHug = 10
const mZoneLabelInset = 12
const mMarks = buildMarks(16)
const mCorner = mPanelRadius

function mNodePos(index: number) {
  const zone = Math.floor(index / 2)
  const slot = index % 2
  return {
    slot,
    x: mZoneX + mNodeInset + slot * (mNodeWidth + mNodeGap),
    y: at(mZoneY, zone) + mZoneHeaderHeight + mNodeInset,
  }
}

function mNodeCenterY(index: number) {
  return mNodePos(index).y + mNodeHeight / 2
}

function mClientCenterX(index: number) {
  return at(mClientXs, index) + at(mClientWidths, index) / 2
}

// Client traffic takes the outer left gutter and enters each zone at its
// top-left corner, away from the store traffic on the bottom and right.
function mRequestPath(clientIndex: number, zone: number) {
  const topY = at(mZoneY, zone) + mZoneHeaderHeight
  const cx = mClientCenterX(clientIndex)
  const r = mCorner
  return `M${cx} ${mClientExitY} V${mGutterTopY - r} A${r} ${r} 0 0 1 ${cx - r} ${mGutterTopY} H${mLeftGutterX + r} A${r} ${r} 0 0 0 ${mLeftGutterX} ${mGutterTopY + r} V${topY - r} A${r} ${r} 0 0 0 ${mLeftGutterX + r} ${topY} H${mZoneX}`
}

function mResponsePath(clientIndex: number, zone: number) {
  const topY = at(mZoneY, zone) + mZoneHeaderHeight
  const cx = mClientCenterX(clientIndex)
  const r = mCorner
  return `M${mZoneX} ${topY} H${mLeftGutterX + r} A${r} ${r} 0 0 1 ${mLeftGutterX} ${topY - r} V${mGutterTopY + r} A${r} ${r} 0 0 1 ${mLeftGutterX + r} ${mGutterTopY} H${cx - r} A${r} ${r} 0 0 0 ${cx} ${mGutterTopY - r} V${mClientExitY}`
}

// Node 1 of each zone exits left into the inner left gutter, node 2 exits
// right into the right gutter; the two meet once, at the drop into the store.
function mNodeStub(index: number) {
  const { x, slot } = mNodePos(index)
  const cy = mNodeCenterY(index)
  const gutterX = slot === 0 ? mLeftStoreGutterX : mRightGutterX
  const edgeX = slot === 0 ? x : x + mNodeWidth
  const approachX = slot === 0 ? gutterX + mCorner : gutterX - mCorner
  const sweep = slot === 0 ? 0 : 1
  return `M${edgeX} ${cy} H${approachX} A${mCorner} ${mCorner} 0 0 ${sweep} ${gutterX} ${cy + mCorner}`
}

function mNodeStorePath(index: number) {
  const { slot } = mNodePos(index)
  const gutterX = slot === 0 ? mLeftStoreGutterX : mRightGutterX
  const cornerX = slot === 0 ? gutterX + mCorner : gutterX - mCorner
  const sweep = slot === 0 ? 0 : 1
  return `${mNodeStub(index)} V${mStoreMergeY - mCorner} A${mCorner} ${mCorner} 0 0 ${sweep} ${cornerX} ${mStoreMergeY} H${mStoreCenterX} V${mStoreY - arrowOverhang}`
}

const mTrunkPaths = [
  `M${mLeftStoreGutterX} ${mNodeCenterY(0)} V${mStoreMergeY - mCorner} A${mCorner} ${mCorner} 0 0 0 ${mLeftStoreGutterX + mCorner} ${mStoreMergeY} H${mStoreCenterX}`,
  `M${mRightGutterX} ${mNodeCenterY(1)} V${mStoreMergeY - mCorner} A${mCorner} ${mCorner} 0 0 1 ${mRightGutterX - mCorner} ${mStoreMergeY} H${mStoreCenterX}`,
  `M${mStoreCenterX} ${mStoreMergeY} V${mStoreY - arrowOverhang - fabricTail}`,
]
const mTrunkTailPath = `M${mStoreCenterX} ${mStoreY - arrowOverhang - fabricTail} V${mStoreY - arrowOverhang}`

const mStaticRoutes = [0, 1, 2].map((i) => mRequestPath(i, i))
const mFabricFlows = buildFabricFlows(
  nodeLabels.map((_, index) => mNodeStorePath(index)),
  mFlowSpeed,
)

const stackedTravel: TravelTime = (clientIndex, nodeIndex) =>
  travelMs(mRequestPath(clientIndex, Math.floor(nodeIndex / 2)), mFlowDotLength, mFlowSpeed)

// ---------- simulation ----------

const tickMs = 200
const faultIntervalMs = 2600
const maxActiveFaults = 2
const faultMinMs = 3600
const faultMaxMs = 5200
const faultRecoveryMs = 700
// How long a route lingers after its dot arrives, and how long a refused
// operation shows its fault mark.
const opHoldMs = 300
const opFailHoldMs = 600
const opGapMinMs = 300
const opGapMaxMs = 800
const initialSeed = 0x9e3779b9

function randomBetween(random: Random, min: number, max: number) {
  return min + random.next() * (max - min)
}

function pickIndex(random: Random, candidates: number[]) {
  return at(candidates, Math.floor(random.next() * candidates.length))
}

function createInitialState(): SimulationState {
  return {
    elapsed: 0,
    links: nodeLabels.map(() => 0),
    nextFaultAt: faultIntervalMs,
    nodes: nodeLabels.map(() => ({ kind: "none", until: 0 })),
    ops: clientLabels.map((_, index) => ({
      arrivesAt: 0,
      failed: false,
      isActive: false,
      isResponse: false,
      markDelayMs: 0,
      readyAt: 200 + index * 450,
      serial: 0,
      target: index * 2,
    })),
    seed: initialSeed,
  }
}

/** A node serves operations only while it is up and its PostgreSQL link is intact. */
function isNodeServing(nodes: readonly NodeFault[], links: readonly number[], index: number) {
  return at(nodes, index).kind === "none" && at(links, index) === 0
}

function countActiveFaults(nodes: NodeFault[], links: number[]) {
  return nodes.filter((fault) => fault.kind !== "none").length + links.filter(Boolean).length
}

function injectFault(nodes: NodeFault[], links: number[], elapsed: number, random: Random) {
  if (countActiveFaults(nodes, links) >= maxActiveFaults) return

  const roll = random.next()
  const until = elapsed + randomBetween(random, faultMinMs, faultMaxMs)

  if (roll < 0.6) {
    const healthy = nodes.flatMap((fault, index) =>
      fault.kind === "none" && fault.until <= elapsed ? [index] : [],
    )
    if (!healthy.length) return
    nodes[pickIndex(random, healthy)] = { kind: roll < 0.3 ? "down" : "paused", until }
    return
  }

  const intact = links.flatMap((linkUntil, index) => (linkUntil === 0 ? [index] : []))
  if (!intact.length) return
  links[pickIndex(random, intact)] = until
}

function stepOperation(
  op: Operation,
  clientIndex: number,
  elapsed: number,
  nodes: NodeFault[],
  links: number[],
  random: Random,
  travel: TravelTime,
): Operation {
  // A fault that lands while the dot is still in flight fails the operation:
  // a request is refused when it reaches the node, a response is cut off.
  if (
    op.isActive &&
    !op.failed &&
    elapsed < op.arrivesAt &&
    !isNodeServing(nodes, links, op.target)
  ) {
    const markDelayMs = op.isResponse ? 0 : op.arrivesAt - elapsed
    return { ...op, failed: true, markDelayMs, readyAt: elapsed + markDelayMs + opFailHoldMs }
  }

  if (elapsed < op.readyAt) return op

  if (op.isActive) {
    return {
      ...op,
      isActive: false,
      readyAt: elapsed + randomBetween(random, opGapMinMs, opGapMaxMs),
    }
  }

  const target = Math.floor(random.next() * nodes.length)
  const failed = !isNodeServing(nodes, links, target)
  const travelDuration = travel(clientIndex, target)

  return {
    arrivesAt: elapsed + travelDuration,
    failed,
    isActive: true,
    isResponse: !failed && clientIndex !== pushClientIndex,
    markDelayMs: failed ? travelDuration : 0,
    readyAt: elapsed + travelDuration + (failed ? opFailHoldMs : opHoldMs),
    serial: op.serial + 1,
    target,
  }
}

function stepSimulation(
  state: SimulationState,
  deltaMs: number,
  travel: TravelTime,
): SimulationState {
  const random = createRandom(state.seed)
  const elapsed = state.elapsed + deltaMs
  const nodes = state.nodes.map<NodeFault>((fault) =>
    fault.kind !== "none" && fault.until <= elapsed
      ? { kind: "none", until: elapsed + faultRecoveryMs }
      : fault,
  )
  const links = state.links.map((until) => (until !== 0 && until <= elapsed ? 0 : until))
  let nextFaultAt = state.nextFaultAt

  if (elapsed >= nextFaultAt) {
    injectFault(nodes, links, elapsed, random)
    nextFaultAt = elapsed + faultIntervalMs
  }

  const ops = state.ops.map((op, index) =>
    stepOperation(op, index, elapsed, nodes, links, random, travel),
  )

  return { elapsed, links, nextFaultAt, nodes, ops, seed: random.seed }
}

// ---------- hooks ----------

// ---------- rendering ----------

interface SceneProps {
  arrowId: string
  showsStaticRoutes: boolean
  state: SimulationState
}

function nodeClassName(fault: NodeFault | undefined) {
  const kind = fault?.kind ?? "none"
  return kind === "none" ? "ats-node" : `ats-node ats-node--${kind}`
}

function isLinkLive(state: SimulationState, index: number) {
  return isNodeServing(state.nodes, state.links, index)
}

function ArrowDefs({ id }: { id: string }) {
  return (
    <defs>
      <marker
        id={`${id}-route`}
        viewBox="0 0 10 10"
        refX="8.5"
        refY="5"
        markerWidth="6"
        markerHeight="6"
        orient="auto-start-reverse"
      >
        <path className="ats-arrowhead ats-arrowhead--route" d="M0 .8 9.5 5 0 9.2z" />
      </marker>
      <marker
        id={`${id}-wire`}
        viewBox="0 0 10 10"
        refX="8.5"
        refY="5"
        markerWidth="6"
        markerHeight="6"
        orient="auto-start-reverse"
      >
        <path className="ats-arrowhead" d="M0 .8 9.5 5 0 9.2z" />
      </marker>
    </defs>
  )
}

function LandscapeScene({ arrowId, showsStaticRoutes, state }: SceneProps) {
  return (
    <svg
      className="ats-scene ats-scene--landscape"
      viewBox={`0 ${sceneTop} ${sceneWidth} ${sceneHeight}`}
      aria-hidden="true"
    >
      <ArrowDefs id={arrowId} />

      <g data-tip-title={clientsTipTitle} data-tip={clientsTip}>
        {clientLabels.map((label, index) => (
          <g key={label}>
            <rect
              className="ats-chip"
              x={at(clientXs, index)}
              y={clientTop}
              width={at(clientWidths, index)}
              height={clientHeight}
              rx={panelRadius}
            />
            <text
              className="ats-name"
              x={clientCenterX(index)}
              y={centredBaseline(clientTop, clientHeight, fontSize)}
              textAnchor="middle"
            >
              {label}
            </text>
          </g>
        ))}
      </g>

      <rect
        className="ats-cell__band"
        x={cellX}
        y={cellHeaderY}
        width={cellWidth}
        height={cellBottom - cellHeaderY}
        rx={cellHeaderRadius}
      />
      <rect
        className="ats-cell__card"
        x={cellX}
        y={cellBodyTop}
        width={cellWidth}
        height={cellBottom - cellBodyTop}
        rx={cellBodyRadius}
      />
      <rect
        className="ats-outline"
        x={cellX}
        y={cellHeaderY}
        width={cellWidth}
        height={cellBottom - cellHeaderY}
        rx={cellHeaderRadius}
      />
      <path className="ats-seam" d={cardTopEdge(cellX, cellBodyTop, cellWidth, cellBodyRadius)} />

      {zoneLabels.map((label, index) => {
        const x = at(zoneX, index)
        return (
          <g key={label}>
            <rect
              className="ats-zone__band"
              x={x}
              y={zoneTop}
              width={zoneWidth}
              height={zoneHeight}
              rx={zoneRadius}
            />
            <rect
              className="ats-zone__card"
              x={x}
              y={zoneCardTop}
              width={zoneWidth}
              height={zoneTop + zoneHeight - zoneCardTop}
              rx={zoneRadius}
            />
            <rect
              className="ats-outline"
              x={x}
              y={zoneTop}
              width={zoneWidth}
              height={zoneHeight}
              rx={zoneRadius}
            />
            <path className="ats-seam" d={cardTopEdge(x, zoneCardTop, zoneWidth, zoneRadius)} />
            <text
              className="ats-label"
              x={x + zoneLabelInset}
              y={centredBaseline(zoneTop, zoneBandHeight, fontSize)}
            >
              {label}
            </text>
          </g>
        )
      })}

      <g>
        {nodeLabels.map((label, index) => {
          const isCut = (state.links[index] ?? 0) !== 0
          const flow = at(fabricFlows, index)
          return (
            <g key={label}>
              <path
                className={`ats-wire${isCut ? " ats-wire--cut" : ""}`}
                d={nodeLinkPath(index)}
              />
              {!showsStaticRoutes && isLinkLive(state, index) ? (
                <LoopFlow flow={flow} speed={flowSpeed} dotLength={flowDotLength} />
              ) : null}
            </g>
          )
        })}
        <path className="ats-wire" d={`M${nodeCenterX(0) + routeCorner} ${busY} H${cellCenterX}`} />
        <path
          className="ats-wire"
          d={`M${nodeCenterX(nodeCount - 1) - routeCorner} ${busY} H${cellCenterX}`}
        />
        <path
          className="ats-wire"
          d={`M${cellCenterX} ${busY} V${storeTop - arrowOverhang - fabricTail}`}
        />
        <path
          className="ats-wire ats-wire--tail"
          markerEnd={`url(#${arrowId}-wire)`}
          d={`M${cellCenterX} ${storeTop - arrowOverhang - fabricTail} V${storeTop - arrowOverhang}`}
        />
      </g>

      <rect
        className="ats-chip"
        x={cellCenterX - storeWidth / 2}
        y={storeTop}
        width={storeWidth}
        height={storeHeight}
        rx={storeRadius}
      />
      <Database
        className="ats-icon"
        x={cellCenterX - storeWidth / 2 + storeHug}
        y={storeTop + storeHeight / 2 - storeIconSize / 2}
        size={storeIconSize}
        aria-hidden="true"
      />
      <text
        className="ats-label ats-label--strong"
        x={cellCenterX - storeWidth / 2 + storeHug + storeIconSize + storeIconGap}
        y={centredBaseline(storeTop, storeHeight, fontSize)}
      >
        {storeLabel}
      </text>

      {nodeLabels.map((label, index) => {
        const x = at(nodeXs, index)
        const centerX = nodeCenterX(index)
        return (
          <g key={label} className={nodeClassName(state.nodes[index])}>
            <rect
              className="ats-node__box"
              x={x}
              y={nodeTop}
              width={nodeWidth}
              height={nodeHeight}
              rx={nodeRadius}
            />
            <text
              className="ats-name ats-node__name"
              x={centerX}
              y={centredBaseline(nodeTop, nodeHeight, fontSize)}
              textAnchor="middle"
            >
              {label}
            </text>
            <path
              className="ats-node__kill"
              d={marks.kill}
              transform={`translate(${centerX} ${markCenterY})`}
            />
            <path
              className="ats-node__pause"
              d={marks.pause}
              transform={`translate(${centerX} ${markCenterY})`}
            />
          </g>
        )
      })}

      {showsStaticRoutes ? (
        <g>
          {staticRoutes.map((route) => (
            <path
              key={route}
              className="ats-route ats-route--static"
              d={route}
              markerEnd={`url(#${arrowId}-route)`}
            />
          ))}
        </g>
      ) : (
        state.ops.map((op, index) => {
          const request = requestRoute(index, op.target)
          const flowRoute = op.isResponse ? responseRoute(index, op.target) : request
          // A response cut off mid-flight stops carrying traffic.
          const showsFlow = op.isActive && !(op.failed && op.isResponse)
          return (
            <g
              key={at(clientLabels, index)}
              className={`ats-op${op.isActive ? " ats-op--on" : ""}`}
            >
              <path className="ats-route" d={request} />
              {showsFlow ? (
                <OnceFlow
                  key={op.serial}
                  d={flowRoute}
                  dotLength={flowDotLength}
                  isFault={op.failed}
                  speed={flowSpeed}
                />
              ) : null}
              {op.isActive && op.failed ? (
                <text
                  key={`mark-${op.serial}`}
                  className="ats-op__mark"
                  x={nodeCenterX(op.target)}
                  y={nodeTop - 18}
                  textAnchor="middle"
                  style={{ animationDelay: `${op.markDelayMs}ms` }}
                >
                  ×
                </text>
              ) : null}
            </g>
          )
        })
      )}

      <svg
        x={cellIconX}
        y={cellHeaderY + cellHeaderHeight / 2 - cellIconSize / 2}
        width={cellIconSize}
        height={cellIconSize}
        className="ats-icon"
      >
        <EntireIcon size={cellIconSize} />
      </svg>
      <text
        className="ats-label ats-label--over"
        x={cellLabelX}
        y={centredBaseline(cellHeaderY, cellHeaderHeight, fontSize)}
      >
        {cellLabel}
      </text>
    </svg>
  )
}

function StackedScene({ arrowId, showsStaticRoutes, state }: SceneProps) {
  return (
    <svg
      className="ats-scene ats-scene--stacked"
      viewBox={`0 0 ${mSceneWidth} ${mSceneHeight}`}
      aria-hidden="true"
    >
      <ArrowDefs id={arrowId} />

      <g data-tip-title={clientsTipTitle} data-tip={clientsTip}>
        {clientLabels.map((label, index) => (
          <g key={label}>
            <rect
              className="ats-chip"
              x={at(mClientXs, index)}
              y={mClientY}
              width={at(mClientWidths, index)}
              height={mClientHeight}
              rx={mPanelRadius}
            />
            <text
              className="ats-name"
              x={mClientCenterX(index)}
              y={centredBaseline(mClientY, mClientHeight, mFontSize)}
              textAnchor="middle"
            >
              {label}
            </text>
          </g>
        ))}
      </g>

      <rect
        className="ats-cell__band"
        x={mCellX}
        y={mCellY}
        width={mCellWidth}
        height={mCellBottom - mCellY}
        rx={mRadius}
      />
      <rect
        className="ats-cell__card"
        x={mCellX}
        y={mCellHeaderBottom}
        width={mCellWidth}
        height={mCellBottom - mCellHeaderBottom}
        rx={mRadius}
      />
      <rect
        className="ats-outline"
        x={mCellX}
        y={mCellY}
        width={mCellWidth}
        height={mCellBottom - mCellY}
        rx={mRadius}
      />
      <path className="ats-seam" d={cardTopEdge(mCellX, mCellHeaderBottom, mCellWidth, mRadius)} />

      {zoneLabels.map((label, index) => {
        const y = at(mZoneY, index)
        return (
          <g key={label}>
            <rect
              className="ats-zone__band"
              x={mZoneX}
              y={y}
              width={mZoneWidth}
              height={mZoneHeight}
              rx={mRadius}
            />
            <rect
              className="ats-zone__card"
              x={mZoneX}
              y={y + mZoneHeaderHeight}
              width={mZoneWidth}
              height={mZoneBodyHeight}
              rx={mRadius}
            />
            <rect
              className="ats-outline"
              x={mZoneX}
              y={y}
              width={mZoneWidth}
              height={mZoneHeight}
              rx={mRadius}
            />
            <path
              className="ats-seam"
              d={cardTopEdge(mZoneX, y + mZoneHeaderHeight, mZoneWidth, mRadius)}
            />
            <text
              className="ats-label"
              x={mZoneX + mZoneLabelInset}
              y={centredBaseline(y, mZoneHeaderHeight, mFontSize)}
            >
              {label}
            </text>
          </g>
        )
      })}

      <g>
        {nodeLabels.map((label, index) => {
          const isCut = (state.links[index] ?? 0) !== 0
          return (
            <g key={label}>
              <path className={`ats-wire${isCut ? " ats-wire--cut" : ""}`} d={mNodeStub(index)} />
              {!showsStaticRoutes && isLinkLive(state, index) ? (
                <LoopFlow
                  flow={at(mFabricFlows, index)}
                  speed={mFlowSpeed}
                  dotLength={mFlowDotLength}
                />
              ) : null}
            </g>
          )
        })}
        {mTrunkPaths.map((d) => (
          <path key={d} className="ats-wire" d={d} />
        ))}
        <path
          className="ats-wire ats-wire--tail"
          markerEnd={`url(#${arrowId}-wire)`}
          d={mTrunkTailPath}
        />
      </g>

      <rect
        className="ats-chip"
        x={mStoreX}
        y={mStoreY}
        width={mStoreWidth}
        height={mStoreHeight}
        rx={mPanelRadius}
      />
      <Database
        className="ats-icon"
        x={mStoreX + mStoreHug}
        y={mStoreY + mStoreHeight / 2 - mStoreIconSize / 2}
        size={mStoreIconSize}
        aria-hidden="true"
      />
      <text
        className="ats-label ats-label--strong"
        x={mStoreX + mStoreHug + mStoreIconSize + mStoreIconGap}
        y={centredBaseline(mStoreY, mStoreHeight, mFontSize)}
      >
        {storeLabel}
      </text>

      {nodeLabels.map((label, index) => {
        const pos = mNodePos(index)
        const cx = pos.x + mNodeWidth / 2
        const cy = mNodeCenterY(index)
        return (
          <g key={label} className={nodeClassName(state.nodes[index])}>
            <rect
              className="ats-node__box"
              x={pos.x}
              y={pos.y}
              width={mNodeWidth}
              height={mNodeHeight}
              rx={mPanelRadius}
            />
            <text
              className="ats-name ats-node__name"
              x={cx}
              y={centredBaseline(pos.y, mNodeHeight, mFontSize)}
              textAnchor="middle"
            >
              {label}
            </text>
            <path className="ats-node__kill" d={mMarks.kill} transform={`translate(${cx} ${cy})`} />
            <path
              className="ats-node__pause"
              d={mMarks.pause}
              transform={`translate(${cx} ${cy})`}
            />
          </g>
        )
      })}

      {showsStaticRoutes ? (
        <g>
          {mStaticRoutes.map((route) => (
            <path
              key={route}
              className="ats-route ats-route--static"
              d={route}
              markerEnd={`url(#${arrowId}-route)`}
            />
          ))}
        </g>
      ) : (
        state.ops.map((op, index) => {
          const zone = Math.floor(op.target / 2)
          const request = mRequestPath(index, zone)
          const flowRoute = op.isResponse ? mResponsePath(index, zone) : request
          // A response cut off mid-flight stops carrying traffic.
          const showsFlow = op.isActive && !(op.failed && op.isResponse)
          return (
            <g
              key={at(clientLabels, index)}
              className={`ats-op${op.isActive ? " ats-op--on" : ""}`}
            >
              <path className="ats-route" d={request} />
              {showsFlow ? (
                <OnceFlow
                  key={op.serial}
                  d={flowRoute}
                  dotLength={mFlowDotLength}
                  isFault={op.failed}
                  speed={mFlowSpeed}
                />
              ) : null}
            </g>
          )
        })
      )}

      <svg
        x={mCellX + mCellIconHug}
        y={mCellY + mCellHeaderHeight / 2 - mCellIconSize / 2}
        width={mCellIconSize}
        height={mCellIconSize}
        className="ats-icon"
      >
        <EntireIcon size={mCellIconSize} />
      </svg>
      <text
        className="ats-label ats-label--over"
        x={mCellX + mCellIconHug + mCellIconSize + mCellIconGap}
        y={centredBaseline(mCellY, mCellHeaderHeight, mFontSize)}
      >
        {cellLabel}
      </text>
    </svg>
  )
}

export function AntithesisSimulation() {
  const rootRef = useRef<HTMLElement>(null)
  const arrowId = `ats-${useId().replace(/:/g, "")}`
  const [state, setState] = useState<SimulationState>(createInitialState)
  const [isPaused, setIsPaused] = useState(false)
  const width = useElementWidth(rootRef, null)
  const isVisible = useIsVisible(rootRef)
  const prefersReducedMotion = usePrefersReducedMotion()
  const isStacked = width !== null && width < stackedBreakpoint
  const isRunning = isVisible && !isPaused && !prefersReducedMotion

  useEffect(() => {
    if (!isRunning) return

    const travel = isStacked ? stackedTravel : landscapeTravel
    const interval = window.setInterval(
      () => setState((current) => stepSimulation(current, tickMs, travel)),
      tickMs,
    )
    return () => window.clearInterval(interval)
  }, [isRunning, isStacked])

  const Scene = isStacked ? StackedScene : LandscapeScene

  return (
    <figure
      ref={rootRef}
      data-layout={isStacked ? "stacked" : "landscape"}
      data-running={isRunning ? "true" : "false"}
      className={`ats-root not-prose my-10 overflow-hidden rounded-lg bg-floating font-sans ${cardRingClassName}`}
      aria-label="Deterministic simulation testing with Antithesis. Three Git clients (push, fetch and clone) keep sending operations into an EntireDB cell of six nodes across availability zones AZ-A, AZ-B and AZ-C, all sharing one PostgreSQL, while Antithesis kills nodes, pauses them and cuts node-to-database links."
    >
      <div className="flex h-10 items-center gap-2 border-b border-default px-4 text-xs leading-4">
        <span className="shrink-0 text-muted">Antithesis</span>
        <span className="min-w-0 flex-1 truncate text-right font-semibold text-default">
          Deterministic simulation testing
        </span>
      </div>

      <div className="ats-stage">
        <Scene arrowId={arrowId} showsStaticRoutes={prefersReducedMotion} state={state} />
      </div>

      <div className="flex items-center gap-4 border-t border-default px-4 py-3">
        <figcaption className="min-w-0 flex-1 text-sm text-muted">{antithesisCaption}</figcaption>
        <button
          type="button"
          className="ats-playback shrink-0"
          onClick={() => setIsPaused((paused) => !paused)}
          disabled={prefersReducedMotion}
          aria-label={isPaused ? "Play simulation" : "Pause simulation"}
        >
          {isPaused || prefersReducedMotion ? (
            <Play size={14} aria-hidden="true" />
          ) : (
            <Pause size={14} aria-hidden="true" />
          )}
        </button>
      </div>
      <DiagramTooltip rootRef={rootRef} />
    </figure>
  )
}
